<template>
  <div class="lp-hdr ag-cell-label-container d-flex align-center" role="presentation">
    <button
      type="button"
      class="ag-header-icon lp-menu-trigger"
      :aria-label="$t('actions.menu')"
      @click.stop="openColumnMenu"
    >
      <span class="ag-icon ag-icon-menu" aria-hidden="true" />
    </button>
    <div
      class="ag-header-cell-label lp-label flex-grow-1"
      role="columnheader"
      :aria-sort="sortDir === 'asc' ? 'ascending' : sortDir === 'desc' ? 'descending' : 'none'"
      tabindex="0"
      @click.stop="onSort"
      @keydown.enter.prevent="onSort"
      @keydown.space.prevent="onSort"
    >
      <span class="ag-header-cell-text text-truncate">
        {{ displayName }}
      </span>

      <span class="lp-sort-icons">
        <v-icon
          v-if="sortDir === 'asc'"
          small
          class="lp-sort-icon lp-sort-icon--asc"
          aria-hidden="true"
          >mdi-arrow-up</v-icon
        >

        <v-icon
          v-if="sortDir === 'desc'"
          small
          class="lp-sort-icon lp-sort-icon--desc"
          aria-hidden="true"
          >mdi-arrow-down</v-icon
        >

        <v-icon v-if="filterActive" small class="lp-filter-icon" aria-hidden="true"
          >mdi-filter-outline</v-icon
        >
      </span>
    </div>

    <v-menu offset-y transition="fade-transition" :min-width="160" origin="top left" @click.stop>
      <template v-slot:activator="{ on, attrs }">
        <v-btn
          icon
          small
          :ripple="false"
          class="lp-icon-btn order-last mr-1"
          v-bind="attrs"
          :aria-label="$t('actions.more')"
          v-on="on"
          @click.stop
        >
          <v-icon small>mdi-dots-vertical</v-icon>
        </v-btn>
      </template>

      <v-list dense class="py-0">
        <v-list-item @click.stop="emitAction('create')">
          <v-list-item-title>{{
            $t('settings.architecture.linePricingGroups.dialog.createNewGroup')
          }}</v-list-item-title>
        </v-list-item>
        <v-list-item @click.stop="emitAction('edit')">
          <v-list-item-title>{{
            $t('settings.architecture.linePricingGroups.dialog.editGroup')
          }}</v-list-item-title>
        </v-list-item>

        <v-divider class="my-1" />
        <v-list-item @click.stop="emitAction('delete')">
          <v-list-item-title class="error--text">{{
            $t('settings.architecture.linePricingGroups.dialog.deleteGroup')
          }}</v-list-item-title>
        </v-list-item>
      </v-list>
    </v-menu>
  </div>
</template>

<script>
import { get } from 'lodash';

export default {
  name: 'LinePricingGroupColumnHeader',

  data() {
    return {
      sortDir: null,
      sortIndex: null,
      filterActive: false,
    };
  },

  computed: {
    displayName() {
      return (
        get(this.params, 'displayName') ||
        get(this.params, 'label') ||
        this.$t('settings.architecture.linePricingGroupTableHeader')
      );
    },
  },

  mounted() {
    this.updateSortState();
    this.updateFilterState();
    if (this.params && this.params.column) {
      this.params.column.addEventListener('sortChanged', this.updateSortState);
    }
    if (this.params && this.params.api) {
      this.params.api.addEventListener('filterChanged', this.updateFilterState);
    }
  },

  beforeDestroy() {
    if (this.params && this.params.column) {
      this.params.column.removeEventListener('sortChanged', this.updateSortState);
    }
    if (this.params && this.params.api) {
      this.params.api.removeEventListener('filterChanged', this.updateFilterState);
    }
  },

  methods: {
    updateSortState() {
      const col = this.params && this.params.column;
      this.sortDir = col && typeof col.getSort === 'function' ? col.getSort() : null;
      const idx = col && typeof col.getSortIndex === 'function' ? col.getSortIndex() : null;
      this.sortIndex = typeof idx === 'number' ? idx : null;
    },

    updateFilterState() {
      const col = this.params && this.params.column;
      this.filterActive =
        col && typeof col.isFilterActive === 'function' ? !!col.isFilterActive() : false;
    },

    onSort(event) {
      if (!this.params || !this.params.column) return;
      const multi = !!(event && event.shiftKey);

      if (typeof this.params.progressSort === 'function') {
        this.params.progressSort(multi);
        return;
      }

      const col = this.params.column;
      const current = typeof col.getSort === 'function' ? col.getSort() : null;

      // Derive next sort without nested ternary (asc -> desc -> none -> asc)
      let next;
      if (current === 'asc') {
        next = 'desc';
      } else if (current === 'desc') {
        next = null;
      } else {
        next = 'asc';
      }

      if (typeof this.params.setSort === 'function') {
        this.params.setSort(next, multi);
        return;
      }

      if (this.params.api && typeof this.params.api.applyColumnState === 'function') {
        this.params.api.applyColumnState({
          state: [{ colId: col.getColId(), sort: next }],
          applyOrder: true,
        });
      }
    },

    openColumnMenu() {
      if (!this.params || typeof this.params.showColumnMenu !== 'function') return;
      this.params.showColumnMenu(this.$el);
    },

    emitAction(type) {
      const p = this.params || {};
      const tsgKey = p.tsgKey || null;
      if (type === 'create' && typeof p.onCreate === 'function') p.onCreate(tsgKey);
      if (type === 'edit' && typeof p.onEdit === 'function') p.onEdit(tsgKey);
      if (type === 'delete' && typeof p.onDelete === 'function') p.onDelete(tsgKey);
    },
  },
};
</script>

<style scoped lang="scss">
.lp-hdr {
  width: 100%;
  height: 100%;
  padding: 0 2px;
  font-size: 12px;
  font-weight: 600;
  min-width: 0;
  user-select: none;
}

.lp-icon-btn {
  height: 24px !important;
  width: 24px !important;
  min-width: 24px !important;
  padding: 0;
  border-radius: 6px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-width: 1px;
  border-color: #343a3f;

  .mdi-dots-vertical {
    position: relative !important;
    right: auto !important;
  }
}

.lp-menu-trigger {
  background: transparent;
  border: 0;
  padding: 0 2px;
  margin: 0;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  height: 100%;
  line-height: 1;
  opacity: 1;
  color: inherit;
  outline: none;
}

.lp-menu-trigger .ag-icon {
  transition: color 120ms ease;
}

.lp-menu-trigger:hover .ag-icon,
.lp-menu-trigger:focus .ag-icon {
  color: var(--v-primary-base, #1976d2);
  outline: none;
  box-shadow: none !important;
}

.lp-menu-trigger:focus {
  outline: none !important;
  box-shadow: none !important;
}

.lp-label {
  min-width: 0;
  cursor: pointer;
}

.text-truncate {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
